import type { AckPayloads, AudienceInput, LintFinding, LiveAudience, MakerAgent } from '@ralph/contracts'
import { liveAudienceOf } from '@ralph/contracts'
import { ArrowLeft, Lock, RotateCcw, Save, TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { isStaleWrite } from '@/api/agents.ts'
import { ApiRequestError } from '@/api/client.ts'
import type { PickerViewer } from '@/components/groups/audience-picker.ts'
import { type OwnerChoice, OwnerPicker, ownerRefOf } from '@/components/groups/OwnerPicker.tsx'
import {
  backDestination,
  canAdministerAgent,
  canEditAgent,
  isNotAvailable,
  ownsAgent
} from '@/components/marketplace/agent-sheet.ts'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { AgentDetailsSheet } from '@/components/marketplace/consumer/AgentDetailsSheet.tsx'
import { AudienceSection } from '@/components/marketplace/maker/AudienceSection.tsx'
import { BehaviorSection } from '@/components/marketplace/maker/BehaviorSection.tsx'
import { Note, SECTION_IDS, StepNav } from '@/components/marketplace/maker/BuilderShell.tsx'
import {
  audienceInputOf,
  type BuilderDraft,
  draftFromAgent,
  isCompleteAudience,
  isDirty,
  suggestAgentId,
  toUpsert,
  uniqueAgentId
} from '@/components/marketplace/maker/builder-form.ts'
import { CapabilitiesSection } from '@/components/marketplace/maker/CapabilitiesSection.tsx'
import { EditorsPanel } from '@/components/marketplace/maker/EditorsPanel.tsx'
import { HealthPanel } from '@/components/marketplace/maker/HealthPanel.tsx'
import { IdentitySection } from '@/components/marketplace/maker/IdentitySection.tsx'
import { InstructionsSection } from '@/components/marketplace/maker/InstructionsSection.tsx'
import { KnowledgeSection } from '@/components/marketplace/maker/KnowledgeSection.tsx'
import { type CoverageEntry, presentCoverage } from '@/components/marketplace/maker/knowledge-coverage.ts'
import { LifecycleBar } from '@/components/marketplace/maker/LifecycleBar.tsx'
import { OwnerLine } from '@/components/marketplace/maker/OwnerLine.tsx'
import { PreviewPane } from '@/components/marketplace/maker/PreviewPane.tsx'
import { PublishDialog } from '@/components/marketplace/maker/PublishDialog.tsx'
import {
  type DescribeAudience,
  planPublication,
  privateLabelFor,
  privateSubjectFor,
  publishStatements,
  publishWording
} from '@/components/marketplace/maker/publish-wording.ts'
import { StartersSection } from '@/components/marketplace/maker/StartersSection.tsx'
import { VersionsPanel } from '@/components/marketplace/maker/VersionsPanel.tsx'
import { Badge, Button, EmptyState, Input, Spinner } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useGroups } from '@/hooks/use-groups.ts'
import { useAgentCoverage, useKnowledgeBases } from '@/hooks/use-knowledge-bases.ts'
import {
  useCreateMakerAgent,
  useMakerAgent,
  useMakerCapabilities,
  useMakerCategories,
  useSaveMakerAgent
} from '@/hooks/use-maker.ts'
import { useAgentAbout } from '@/hooks/use-marketplace.ts'
import { describeApiError, type Messages, useI18n, useT } from '@/i18n/index.ts'

/** The way back, in the same mono pill every screen in the product uses to go up a level. */
const BACK_PILL =
  'readout inline-flex h-[29px] items-center gap-[7px] rounded-full border border-rule bg-card pr-3 pl-[9px] text-[10px] tracking-[0.05em] text-fg-muted transition-[color,border-color,transform] duration-200 hover:-translate-y-px hover:border-rule-strong hover:text-fg'

/**
 * The builder.
 *
 * Its acceptance test is a DOM audit: no numeric runtime parameter, no model identifier, no
 * risk enum, no tool string, no JSON editor and no layer concept may appear anywhere on
 * this page (AGM-020). That is why the form's model is `BuilderDraft` — the exact shape of
 * the maker-plane write — and why nothing on screen is assembled from an agent body.
 *
 * The sections run in one fixed order with the preview beside them. Nothing is behind an
 * "advanced" disclosure, because the moment a section is hidden the ordering stops being a
 * path a first-time maker can follow to the end — and because six of the seven are queried by
 * the browser suite as mounted regions with no navigation of any kind.
 */
export const AgentBuilderPage = () => {
  const { agentId } = useParams<{ agentId: string }>()
  return agentId ? <SheetScreen agentId={agentId} /> : <CreateScreen />
}

/**
 * One route, three fidelities (PRD-22 DTL-010).
 *
 * The maker read is asked first and the listing read is the fallback, in that order and not
 * the other way round: a reader entitled to the builder gets their form in one round trip,
 * on a page that already fires six queries, and the reader who is not pays a 404 on a route
 * that documents 404 as its designed answer (AGM-002, and `useAgentAbout`'s own docblock).
 * Reading the listing first to learn the role would put a second sequential request in front
 * of every owner's form to spare a status code nobody sees.
 *
 * The fallback is armed only on a refusal. A 500 or a dropped connection must NOT fall
 * through to the read-only sheet — that would answer an outage with a permission story, and
 * send somebody hunting for access they already have (DTL-014).
 */
const SheetScreen = ({ agentId }: { agentId: string }) => {
  const maker = useMakerAgent(agentId)
  const refused = maker.isError && isNotAvailable(maker.error)
  const about = useAgentAbout(refused ? agentId : undefined)

  if (maker.data) return <BuilderScreen agentId={agentId} />
  if (about.data) return <AgentDetailsSheet about={about.data} />

  if (maker.isError && !refused) return <SheetFailure onRetry={() => void maker.refetch()} />
  if (refused && about.isError) return <SheetNotAvailable />
  return <SheetLoading />
}

const SheetLoading = () => {
  // Read in the body rather than captured at import: a module-scope `const strings = …` keeps
  // whichever language was live when the bundle evaluated, for every screen in this file at
  // once, with nothing failing (PRD-27 F-3).
  const strings = useT().marketplace.maker.builder

  return (
    // The spinner is decoration and says so; the sentence beside it is the announcement.
    <p className="readout flex items-center gap-2 px-6 py-6 text-fg-subtle">
      <Spinner size={12} />
      {strings.loading}
    </p>
  )
}

/**
 * The agent is not this person's to open — or does not exist, which reads identically here
 * because AGM-002 requires the two to be indistinguishable.
 *
 * The way out is the point. This screen used to be `loadFailed` plus a **Try again** button
 * that re-requested the same 404 forever, with no link anywhere (PRD-22 F-4).
 */
const SheetNotAvailable = () => {
  // The way out is the consumer gallery's own label, read in the body so it follows a locale
  // switch like everything else on the page (PRD-27 F-3, LOC-012).
  const back = useT().marketplace.gallery.about.back
  const strings = useT().marketplace.maker.builder

  return (
    <div className="flex flex-1 items-center justify-center px-5 py-10">
      <EmptyState
        title={strings.notAvailableTitle}
        body={strings.notAvailableBody}
        action={
          <Link to="/agents">
            <Button size="sm">{back}</Button>
          </Link>
        }
      />
    </div>
  )
}

/** The other half of F-5: an outage, worded as one, with the retry that can actually work. */
const SheetFailure = ({ onRetry }: { onRetry: () => void }) => {
  const loadFailed = useT().marketplace.gallery.details.loadFailed
  const strings = useT().marketplace.maker.builder

  return (
    <div className="flex flex-1 items-center justify-center px-5 py-10">
      <EmptyState
        title={loadFailed.title}
        body={loadFailed.body}
        action={
          <Button size="sm" onClick={onRetry}>
            {strings.tryAgain}
          </Button>
        }
      />
    </div>
  )
}

/**
 * Turns a refused creation into something actionable.
 *
 * A limit and an owner the maker may not create for are the two failures a maker can do
 * something about. A limit's refusal already names whose limit and how full it is ("Finance EMEA
 * already has 25 agents…"), so the server's own sentence is shown — through `describeApiError`,
 * which renders it from the catalogue when the refusal names a `messageKey` and falls back to
 * the server's English when it does not (PRD-27 D-4, F-1).
 *
 * Pure, and handed the WHOLE catalogue rather than reaching for it: `describeApiError` looks up
 * `errors` and `common` as well as this surface's own slice, and a helper reading a
 * module-level capture would refuse in whichever language the bundle evaluated in (F-3).
 */
const explainCreateFailure = (t: Messages, error: unknown): string => {
  const strings = t.marketplace.maker.builder
  if (error instanceof ApiRequestError) {
    if (error.code.includes('quota')) return describeApiError(t, error)
    if (error.status === 403) return strings.notAllowed
  }
  return error instanceof Error ? describeApiError(t, error) : strings.createFailed
}

/**
 * Creating.
 *
 * A name, and — for somebody who manages a group, or an administrator — who it belongs to (PRD-26
 * D-19). The stored identifier is derived from the name and never shown — a maker is asked for a
 * name, not a key — and a collision is resolved by retrying once with a suffixed identifier,
 * because two people are entitled to draft an agent with the same working title. Display-name
 * uniqueness is a PUBLISH-time rule (AGM-005) and belongs there, not in the way of someone
 * starting. The owner's limits are the server's to apply (D-41); its refusal says whose.
 */
const CreateScreen = () => {
  const messages = useT()
  const strings = messages.marketplace.maker.builder
  const navigate = useNavigate()
  const session = useSession()
  const create = useCreateMakerAgent()
  const [displayName, setDisplayName] = useState('')
  const [owner, setOwner] = useState<OwnerChoice | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  const user = session.data?.user ?? null
  const isAdmin = user?.roles.includes('admin') ?? false
  const managedGroups = user?.managedGroups ?? []
  const choosesOwner = user !== null && (isAdmin || managedGroups.length > 0)
  const chosen: OwnerChoice | null = owner ?? (user ? { kind: 'user', id: user.id, name: user.displayName } : null)
  // Omitted for the creator: the server's default, and one field fewer that could disagree.
  const ownerRef = chosen && !(chosen.kind === 'user' && chosen.id === user?.id) ? ownerRefOf(chosen) : undefined

  const submit = async () => {
    const name = displayName.trim()
    if (name === '') {
      setFailed(strings.nameRequired)
      return
    }

    setFailed(null)
    const base = suggestAgentId(name)
    const input = (agentId: string) => ({ agentId, displayName: name, ...(ownerRef ? { owner: ownerRef } : {}) })
    try {
      const created = await create.mutateAsync(input(base))
      navigate(`/agents/${created.agentId}/edit`)
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 409) {
        try {
          const retried = await create.mutateAsync(input(uniqueAgentId(base)))
          navigate(`/agents/${retried.agentId}/edit`)
          return
        } catch (retryError) {
          setFailed(explainCreateFailure(messages, retryError))
          return
        }
      }
      setFailed(explainCreateFailure(messages, error))
    }
  }

  return (
    <div className="fade-in min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[520px] px-5 pt-1 pb-10 sm:px-8">
        <Link to="/agents/mine" className={`${BACK_PILL} mt-3.5 mb-6`}>
          <ArrowLeft size={12} strokeWidth={2.2} aria-hidden />
          {strings.backToList}
        </Link>

        <h1 className="display text-[30px] sm:text-[38px]">{strings.newTitle}</h1>
        <p className="mt-[7px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">{strings.newLead}</p>

        <div className="card rise mt-6 rounded-panel px-4 py-4 sm:px-5">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void submit()
            }}
          >
            {/* A real `<label htmlFor>`: `getByLabel('Name')` is the first thing the maker
                journey touches, and a placeholder inside the field is not a name. */}
            <label htmlFor="new-agent-name" className="eyebrow mb-1.5 block text-fg-muted">
              {strings.nameLabel}
            </label>
            <Input
              id="new-agent-name"
              value={displayName}
              maxLength={120}
              autoFocus
              placeholder={strings.namePlaceholder}
              onChange={(event) => setDisplayName(event.target.value)}
            />

            {choosesOwner && user && (
              <div className="mt-4">
                <OwnerPicker
                  value={chosen}
                  onChange={setOwner}
                  viewer={{ id: user.id, displayName: user.displayName, managedGroups }}
                  allowAnyone={isAdmin}
                  disabled={create.isPending}
                />
                <p className="mt-2 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.ownerLead}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-4 w-full"
              disabled={create.isPending}
              icon={create.isPending ? <Spinner size={13} /> : undefined}
            >
              {create.isPending ? strings.creating : strings.create}
            </Button>
          </form>

          {failed && (
            <p role="alert" className="mt-2.5 text-pretty text-[13px] leading-[1.5] text-alert">
              {failed}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * The builder, for a reader entitled to it.
 *
 * `useMakerAgent` again rather than a prop from `SheetScreen`: it is the same query key, so
 * React Query serves it from cache with no second request, and the component keeps the
 * `refetch` that the conflict path and the version-restore path both need.
 *
 * Who can use it is a choice held beside the draft, not in it (PRD-26 D-40): it changes only at
 * publication, where the review rule splits what goes live now from what waits. The same choice
 * drives the coverage report (GRP-045) — Knowledge and "Who can use it" both measure against what
 * the maker is choosing, not what was last published.
 */
/**
 * The coverage gaps as the consent RECORD keeps them: which base, and who it misses, in counts
 * and names (PRD-27 D-7, D-20).
 *
 * `coverageGaps` renders the same gaps into sentences for the Knowledge step, which is what that
 * step needs. A record cannot hold a sentence — "Legal (8 people)" is an English phrase with an
 * English plural in it, and a record holding it reads back in one language only — so the values
 * travel and the catalogue words them in whichever language is asking.
 */
const coverageGapParams = (entries: readonly CoverageEntry[]): AckPayloads['ack.publish']['gaps'] =>
  entries.flatMap((entry) => {
    const line = presentCoverage(entry)
    if (line.kind !== 'gap') return []
    const missing = line.missing
    return [
      {
        base: entry.name,
        missing:
          missing.kind === 'groups'
            ? {
                kind: 'groups' as const,
                people: missing.people,
                groups: missing.groups.map((group) => ({ name: group.name, memberCount: group.memberCount }))
              }
            : { kind: missing.kind, people: missing.people }
      }
    ]
  })

const BuilderScreen = ({ agentId }: { agentId: string }) => {
  // The publish dialog's sentences count people and join group names, and both follow the
  // reader's language. Read here, in the body, never captured at module scope (PRD-27 F-3).
  const fmt = useI18n()
  // The words, read here beside the formatters and for the same reason (PRD-27 F-3). Three
  // slices, because this page speaks for two namespaces: the builder's own words, and the
  // publish namespace's — whose sentences are persisted as the consent record (AGM-042).
  const strings = fmt.t.marketplace.maker.builder
  const maker = fmt.t.marketplace.maker
  const publishWords = fmt.t.marketplace.publish
  const query = useMakerAgent(agentId)
  const save = useSaveMakerAgent(agentId)
  const session = useSession()
  const capabilities = useMakerCapabilities()
  const categories = useMakerCategories()
  // Names and sizes for the groups the dialog talks about. The same key the picker's list reads,
  // so this costs nothing the picker was not already fetching.
  const groups = useGroups({ limit: 200 })
  // The consent sentence is persisted verbatim as the audit record (AGM-042), so every attached
  // base resolves to its real name; same keys as the Knowledge step, so deduped.
  const maintainBases = useKnowledgeBases('maintain')
  const usableBases = useKnowledgeBases('use')

  const [draft, setDraft] = useState<BuilderDraft | null>(null)
  const [audience, setAudience] = useState<AudienceInput | null>(null)
  const [changeNote, setChangeNote] = useState('')
  const [lint, setLint] = useState<LintFinding[]>([])
  const [publishing, setPublishing] = useState<'publish' | 'restore' | null>(null)
  const seeded = useRef<string | null>(null)

  const agent = query.data ?? null
  const coverage = useAgentCoverage(agentId, audience && isCompleteAudience(audience) ? audience : null)

  // Stable across renders (it only calls setters), so the seeding effect below can depend on
  // it without re-running on every keystroke.
  const seed = useCallback((next: MakerAgent, options: { audience: boolean }) => {
    setDraft(draftFromAgent(next))
    if (options.audience) setAudience(audienceInputOf(next.audience))
    setChangeNote('')
  }, [])

  // Seeds the form once per agent. Re-seeding on every fetch would discard an edit in
  // progress the moment a background refetch landed.
  useEffect(() => {
    if (!agent || seeded.current === agent.agentId) return
    seeded.current = agent.agentId
    seed(agent, { audience: true })
  }, [agent, seed])

  const resync = async () => {
    save.reset()
    const fresh = await query.refetch()
    if (fresh.data) seed(fresh.data, { audience: true })
  }

  // `SheetScreen` decides loading, refusal and outage; what is left here is the one render
  // between the payload landing and the seeding effect filling the form.
  if (!agent || !draft || !audience) return <SheetLoading />

  /*
   * What this reader may do, from the role the SERVER decided (PRD-22 DTL-020). A manager of the
   * group that owns it maintains it as its personal owner does (PRD-26 D-19).
   */
  const role = agent.viewerRole
  const canEdit = canEditAgent(role)
  const canAdminister = canAdministerAgent(role)
  const isStakeholder = ownsAgent(role)
  const disabled = !canEdit
  const dirty = isDirty(draft, agent)

  const user = session.data?.user ?? null
  const viewerId = user?.id ?? null
  const isAdmin = user?.roles.includes('admin') ?? false
  const pickerViewer: PickerViewer = {
    isAdmin,
    groupIds: user?.groups.map((group) => group.id) ?? [],
    managedGroupIds: user?.managedGroups.map((group) => group.id) ?? []
  }

  const update = (patch: Partial<BuilderDraft>) => setDraft((current) => (current ? { ...current, ...patch } : current))

  const capabilityItems = capabilities.data?.items ?? []
  const capabilityById = new Map(capabilityItems.map((capability) => [capability.id, capability]))
  const acts = draft.capabilityIds.some((id) => capabilityById.get(id)?.wording === 'acts')

  // ---- names, for everything the publish dialog says ------------------------------------------

  const coverageEntries = (coverage.data?.bases ?? []).filter((entry) => draft.knowledgeBaseIds.includes(entry.baseId))
  // The consent sentence names what is being shared, so an attachment whose name can no longer be
  // resolved is disclosed as unavailable rather than dropped — under-stating what people are
  // getting is the one failure that sentence must not have.
  const baseNames = new Map<string, string>([
    ...coverageEntries.map((entry) => [entry.baseId, entry.name] as const),
    ...[...(maintainBases.data?.items ?? []), ...(usableBases.data?.items ?? [])].map(
      (base) => [base.id, base.name] as const
    )
  ])
  // Known names and a count of the rest, never the stand-in phrase: "a knowledge base that is no
  // longer available to you" is copy, and a consent record holding it could only ever be read
  // back in the language it was written in (PRD-27 D-7, D-20).
  const knowledgeNames = draft.knowledgeBaseIds
    .map((id) => baseNames.get(id))
    .filter((name): name is string => name !== undefined)
  const unknownBases = draft.knowledgeBaseIds.length - knowledgeNames.length
  const grantedNames = draft.capabilityIds
    .map((id) => capabilityById.get(id)?.name)
    .filter((name): name is string => name !== undefined)
  const unknownCapabilities = draft.capabilityIds.length - grantedNames.length

  // Every group the dialog might name, with its size when anything knows it.
  const groupInfo = new Map<string, { name: string; memberCount: number | null }>()
  for (const group of [...(user?.groups ?? []), ...(user?.managedGroups ?? [])]) {
    groupInfo.set(group.id, { name: group.name, memberCount: null })
  }
  for (const group of [
    ...(groups.data?.items ?? []),
    ...agent.audience.groups,
    ...(coverage.data?.audience.groups ?? [])
  ]) {
    groupInfo.set(group.id, { name: group.name, memberCount: group.memberCount })
  }
  const groupPhrase = (id: string): string | null => {
    const group = groupInfo.get(id)
    if (!group) return null
    return group.memberCount === null
      ? group.name
      : maker.sized(fmt, { name: group.name, memberCount: group.memberCount })
  }
  const groupNames = (ids: readonly string[]) =>
    ids.map((id) => groupInfo.get(id)?.name).filter((name): name is string => Boolean(name))

  // Two of its three words are the groups plane's, so this one takes the whole catalogue.
  const privateLabel = privateLabelFor(fmt.t, agent.owner, viewerId)
  const privateSubject = privateSubjectFor(publishWords, agent.owner, viewerId)
  const describe: DescribeAudience = (target) =>
    target.kind === 'everyone'
      ? publishWords.audienceSubject(fmt, 'everyone', [], privateSubject)
      : target.kind === 'private'
        ? privateSubject
        : // The one `Intl.ListFormat` in the repository, in place of the catalogue `list`
          // join that hardcoded " and " (PRD-27 LOC-016).
          fmt.formatList(target.groupIds.map(groupPhrase).filter((name): name is string => Boolean(name))) ||
          publishWords.audiencePhrase(fmt, 'groups', [], privateLabel)

  // ---- what publishing would do ----------------------------------------------------------------

  const mode = publishing ?? (agent.state === 'archived' ? 'restore' : 'publish')
  const live: LiveAudience = liveAudienceOf(agent.audience)
  const plan = planPublication({
    action: mode,
    state: agent.state,
    live,
    requested: audience,
    viewer: { id: viewerId, isAdmin, managedGroupIds: pickerViewer.managedGroupIds }
  })
  const wording = publishWording(publishWords, plan, {
    action: mode,
    hasPublishedBefore: agent.publishedVersion !== null
  })
  const statements = publishStatements(publishWords, plan, { state: agent.state, action: mode, describe })
  const requestedNames = groupNames(audience.kind === 'groups' ? audience.groupIds : [])
  const personal = agent.owner.kind === 'user' && agent.owner.id === viewerId

  // ---- the disclosure, and the record of it ----------------------------------------------------
  //
  // One shape, rendered once and stored once (PRD-27 D-7). The sentence the maker reads is built
  // from exactly the values that are persisted beside `ackKey`, so an auditor who feeds the stored
  // parameters to the English catalogue gets the English sentence — rather than being asked to
  // trust that two hand-written translations still say the same thing.
  const ackParams: AckPayloads['ack.publish'] = {
    audience: {
      kind: audience.kind,
      groups: requestedNames,
      // Which of the picker's three ways of naming "only its maintainers" applies (D-37). Read
      // from the agent's owner, which is what `privateSubjectFor` reads, so the two agree.
      privateKind:
        audience.kind !== 'private'
          ? null
          : agent.owner.kind === 'group'
            ? 'group_managers'
            : personal
              ? 'self'
              : 'person',
      privateName:
        audience.kind !== 'private' || personal
          ? null
          : agent.owner.kind === 'group'
            ? agent.owner.group.name
            : agent.owner.name
    },
    // Who the sentence is about: the maker themselves when the agent is theirs alone, other
    // people otherwise. A semantic fact, not an English pronoun (D-20).
    subject: audience.kind === 'private' && personal ? 'reader' : 'others',
    bases: knowledgeNames,
    unknownBases,
    capabilities: grantedNames,
    unknownCapabilities,
    gaps: coverageGapParams(coverageEntries)
  }
  const consent = publishWords.consentSentence(fmt, ackParams)

  const blockingLint = lint.some((finding) => finding.blocking)
  const publishBlocked = !canAdminister
    ? publishWords.blockedNotOwner
    : blockingLint
      ? publishWords.blockedByLint
      : !isCompleteAudience(audience)
        ? publishWords.blockedByAudience
        : dirty
          ? publishWords.blockedUnsaved
          : null

  const commit = () =>
    save.mutate(
      { definitionVersion: agent.definitionVersion, input: toUpsert(draft, changeNote) },
      {
        onSuccess: (result) => {
          setLint(result.lint)
          // The choice of who can use it survives a save: it is not part of what was saved.
          seed(result.agent, { audience: false })
        }
      }
    )

  const steps = [
    { id: SECTION_IDS.identity, label: maker.sections.identity.title },
    { id: SECTION_IDS.instructions, label: maker.sections.instructions.title },
    { id: SECTION_IDS.knowledge, label: maker.sections.knowledge.title },
    { id: SECTION_IDS.capabilities, label: maker.sections.capabilities.title },
    { id: SECTION_IDS.behavior, label: maker.sections.behavior.title },
    { id: SECTION_IDS.starters, label: maker.sections.starters.title },
    { id: SECTION_IDS.audience, label: maker.sections.audience.title },
    { id: SECTION_IDS.preview, label: maker.preview.title }
  ]

  return (
    <div className="fade-in min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[1080px] px-4 pt-1 pb-9 sm:px-6 lg:px-8">
        <Link to={backDestination(role)} className={`${BACK_PILL} mt-3.5 mb-5`}>
          <ArrowLeft size={12} strokeWidth={2.2} aria-hidden />
          {strings.backToList}
        </Link>

        <header className="flex items-start gap-3.5 sm:gap-4">
          {/* The agent's own tile, never the orb: the orb marks the model itself, and this is
              a thing a person is making. */}
          <AgentAvatar icon={draft.icon} displayName={draft.displayName} accent={draft.accent} size="lg" />

          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <h1 className="display min-w-0 break-words text-[26px] sm:text-[34px]">
                {draft.displayName || strings.newTitle}
              </h1>
              {/*
                Neutral, never `--alert`. That colour is the product's one channel for
                consequence — an approval waiting, a refusal, spend going the wrong way — and
                an unsaved form is a state the maker is in the middle of, not a consequence.
              */}
              {dirty && <Badge>{strings.unsaved}</Badge>}
            </div>
            {draft.tagline && (
              <p className="mt-2 max-w-[524px] text-pretty text-[13.5px] leading-[1.55] text-fg-muted">
                {draft.tagline}
              </p>
            )}
            {/* "Owned by ⬤ Finance EMEA · Move to a group…" (PRD-26 D-19, D-32). */}
            <div className="mt-2.5">
              <OwnerLine
                agent={agent}
                viewer={{
                  id: viewerId ?? '',
                  displayName: user?.displayName ?? '',
                  isAdmin,
                  managedGroups: user?.managedGroups ?? []
                }}
                canMove={canAdminister && user !== null}
              />
            </div>
          </div>
        </header>

        {disabled && (
          <div className="mt-4">
            <Note tone="info" icon={<Lock size={13} strokeWidth={2} />}>
              {role === 'invited' ? strings.readOnlyInvited : strings.readOnly}
            </Note>
          </div>
        )}

        <div className="mt-4">
          <LifecycleBar
            agent={agent}
            action={wording.action}
            canPublish={canAdminister}
            isStakeholder={isStakeholder}
            viewerId={viewerId}
            onPublish={() => setPublishing('publish')}
            onRestore={() => setPublishing('restore')}
          />
        </div>

        <StepNav steps={steps} />

        <div className="mt-4 grid gap-3.5 xl:grid-cols-[minmax(0,1fr)_25rem]">
          <div className="min-w-0 space-y-3.5">
            <IdentitySection
              step={1}
              draft={draft}
              disabled={disabled}
              categories={categories.data?.items ?? []}
              lint={lint}
              onChange={update}
            />
            <InstructionsSection step={2} draft={draft} disabled={disabled} lint={lint} onChange={update} />
            <KnowledgeSection
              step={3}
              agentId={agent.agentId}
              agentName={draft.displayName || agent.displayName}
              owner={agent.owner}
              acts={acts}
              draft={draft}
              savedIds={agent.knowledgeBaseIds}
              coverage={{
                data: coverage.data,
                isLoading: coverage.isLoading,
                isFetching: coverage.isFetching,
                isError: coverage.isError
              }}
              disabled={disabled}
              onChange={update}
            />
            <CapabilitiesSection
              step={4}
              draft={draft}
              disabled={disabled}
              capabilities={capabilityItems}
              loading={capabilities.isLoading}
              failed={capabilities.isError}
              onChange={update}
            />
            <BehaviorSection step={5} draft={draft} disabled={disabled} onChange={update} />
            <StartersSection step={6} draft={draft} disabled={disabled} lint={lint} onChange={update} />
            <AudienceSection
              step={7}
              agent={agent}
              audience={audience}
              onAudienceChange={setAudience}
              viewer={pickerViewer}
              privateLabel={privateLabel}
              coverage={coverage.data}
              draft={draft}
              disabled={disabled}
              canChangeAudience={canAdminister}
              onChange={update}
            />

            {isStaleWrite(save.error) && (
              <div className="card rounded-panel border-alert/40 bg-alert-soft px-4 py-3.5">
                <p className="flex items-center gap-2 text-[13.5px] font-semibold tracking-[-0.014em]">
                  <TriangleAlert size={13} strokeWidth={2.1} aria-hidden className="text-alert" />
                  {strings.conflictTitle}
                </p>
                <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.conflictBody}</p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => void resync()}>
                    {strings.conflictReload}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => save.reset()}>
                    {strings.conflictKeep}
                  </Button>
                </div>
              </div>
            )}

            {save.isError && !isStaleWrite(save.error) && (
              <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
                {strings.saveFailed} {describeApiError(fmt.t, save.error)}
              </p>
            )}

            {canEdit && (
              /*
                The save bar floats over the column rather than sitting behind it: the panels
                scroll under an opaque card with its own lift, which is the only version of a
                sticky footer that survives a translucent stage.
              */
              <div className="sticky bottom-2 z-20 pt-1">
                <div className="card rounded-panel bg-card-solid px-3 py-2.5 shadow-lift">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Everything in this bar is 34px. It is the one row on the page that has
                        to be usable with a thumb, and the primitive's `sm` step is 30. */}
                    <Input
                      value={changeNote}
                      placeholder={strings.changeNotePlaceholder}
                      aria-label={strings.changeNoteLabel}
                      className="min-w-[11rem] flex-1"
                      onChange={(event) => setChangeNote(event.target.value)}
                    />
                    {save.isSuccess && !dirty && <span className="readout text-grounded">{strings.saved}</span>}
                    <Button
                      variant="ghost"
                      icon={<RotateCcw size={13} strokeWidth={2.1} aria-hidden />}
                      disabled={!dirty}
                      onClick={() => seed(agent, { audience: false })}
                    >
                      {strings.revert}
                    </Button>
                    <Button
                      variant="primary"
                      icon={save.isPending ? <Spinner size={13} /> : <Save size={13} strokeWidth={2.1} aria-hidden />}
                      disabled={!dirty || save.isPending}
                      onClick={commit}
                    >
                      {save.isPending ? strings.saving : strings.save}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="min-w-0 space-y-3.5 xl:sticky xl:top-4 xl:self-start">
            <div id={SECTION_IDS.preview} className="scroll-mt-4">
              <PreviewPane
                agentId={agent.agentId}
                icon={draft.icon}
                displayName={draft.displayName}
                accent={draft.accent}
                starters={draft.starters}
                dirty={dirty}
                canPreview={canEdit}
              />
            </div>
            <HealthPanel health={agent.health} />
            <VersionsPanel agentId={agent.agentId} canEdit={canEdit} onRestored={() => void resync()} />
            <EditorsPanel agent={agent} canManage={canAdminister} role={role} />
          </div>
        </div>
      </div>

      {publishing && (
        <PublishDialog
          agent={agent}
          mode={publishing}
          audience={audience}
          plan={plan}
          wording={wording}
          audiencePhrase={publishWords.audiencePhrase(fmt, audience.kind, requestedNames, privateLabel)}
          statements={statements}
          consent={consent}
          ackParams={ackParams}
          who={plan.now ? describe(plan.now) : describe(liveAudienceOf(agent.audience))}
          personal={personal}
          blocked={publishBlocked}
          measuring={coverage.isFetching}
          onClose={() => setPublishing(null)}
          onDone={() => {
            // What went live is what the picker should now hold; what waits is on the bar.
            void query.refetch().then((fresh) => {
              if (fresh.data) setAudience(audienceInputOf(fresh.data.audience))
            })
          }}
        />
      )}
    </div>
  )
}
